/**
 * React Query hook for fetching GitHub repos
 * Story 226: Projects Dashboard
 * Story 585: typed errors, no sign-in redirect, install link
 */

import { useQuery } from '@tanstack/react-query';
import { config } from '../config';

/** HTTP status the listing returns when the GitHub token is gone */
const HTTP_UNAUTHORIZED = 401;

/** GitHub repo item from API */
export interface GitHubRepoListItem {
  id: number;
  full_name: string;
  name: string;
  owner: string;
  is_existing_project: boolean;
}

/** Response from GET /api/v1/github/repos */
export interface GitHubRepoListResponse {
  repos: GitHubRepoListItem[];
  /** GitHub page where the user installs the Ceetrix App or chooses more repos */
  install_url: string;
}

/** Why the listing could not be loaded */
export interface GitHubRepoListError {
  type: 'auth_expired' | 'unavailable';
}

/** Query key factory for GitHub repos */
export const githubRepoKeys = {
  all: ['github-repos'] as const,
  list: () => [...githubRepoKeys.all, 'list'] as const,
};

/**
 * Fetch GitHub repos for the current user.
 *
 * Uses fetch directly rather than api.fetch, which sends the whole page to
 * sign-in on 401; the add-repository window shows its own message instead.
 */
async function fetchGitHubRepos(): Promise<GitHubRepoListResponse> {
  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}/api/v1/github/repos`, {
      credentials: 'include',
    });
  } catch {
    throw { type: 'unavailable' } satisfies GitHubRepoListError;
  }

  if (!response.ok) {
    // Read the body so the request completes; an unread body leaves the fetch open
    await response.text().catch(() => '');
    throw {
      type: response.status === HTTP_UNAUTHORIZED ? 'auth_expired' : 'unavailable',
    } satisfies GitHubRepoListError;
  }

  try {
    return await response.json();
  } catch {
    throw { type: 'unavailable' } satisfies GitHubRepoListError;
  }
}

/**
 * Hook to fetch GitHub repos user has access to
 *
 * @param enabled - Whether to fetch (default false, enable when the window opens)
 */
export function useGitHubRepos(enabled = false) {
  return useQuery<GitHubRepoListResponse, GitHubRepoListError>({
    queryKey: githubRepoKeys.list(),
    queryFn: fetchGitHubRepos,
    enabled,
    retry: false,
  });
}
